import { useState, useMemo } from 'react';
import { Send, CheckCircle, AlertCircle, X, Clock } from 'lucide-react';

// ── Constants ──────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s.\-()]{8,}$/;

const DURATION_MINUTES = 30;

const FR_MONTHS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

const FR_DAYS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

// Template slots: dayOfWeek uses JS convention (0=Sun … 6=Sat)
const SLOT_TEMPLATES = [
  { dayOfWeek: 2, hour: 10, minute: 0  },
  { dayOfWeek: 2, hour: 14, minute: 30 },
  { dayOfWeek: 3, hour: 11, minute: 0  },
  { dayOfWeek: 4, hour: 15, minute: 0  },
  { dayOfWeek: 5, hour: 9,  minute: 30 },
] as const;

function isFrenchSummerTime(date: Date): boolean {
  const year = date.getUTCFullYear();
  const marchEnd = new Date(Date.UTC(year, 2, 31));
  marchEnd.setUTCDate(31 - marchEnd.getUTCDay());
  marchEnd.setUTCHours(1, 0, 0, 0);
  const octEnd = new Date(Date.UTC(year, 9, 31));
  octEnd.setUTCDate(31 - octEnd.getUTCDay());
  octEnd.setUTCHours(1, 0, 0, 0);
  return date >= marchEnd && date < octEnd;
}

interface ComputedSlot {
  labelDay: string;   // "Mardi"
  labelDate: string;  // "28 avril"
  labelTime: string;  // "14h30"
  label: string;      // "Mardi 28 avril à 14h30"
  value: string;      // "2026-04-28-1430"
  startDateTime: string;
  endDateTime: string;
}

function buildSlots(): ComputedSlot[] {
  const pad = (n: number) => String(n).padStart(2, '0');
  const today = new Date();

  return SLOT_TEMPLATES.map((tpl) => {
    let daysUntil = tpl.dayOfWeek - today.getDay();
    if (daysUntil < 0) daysUntil += 7;
    if (daysUntil === 0) {
      const nowMin = today.getHours() * 60 + today.getMinutes();
      if (nowMin >= tpl.hour * 60 + tpl.minute) daysUntil = 7;
    }

    const slotDate = new Date(today);
    slotDate.setDate(today.getDate() + daysUntil);

    const offsetHours = isFrenchSummerTime(slotDate) ? 2 : 1;
    const offsetMin = offsetHours * 60;
    const offsetStr = offsetHours === 2 ? '+02:00' : '+01:00';

    const startUtcMs =
      Date.UTC(
        slotDate.getFullYear(),
        slotDate.getMonth(),
        slotDate.getDate(),
        tpl.hour,
        tpl.minute,
        0,
      ) - offsetMin * 60_000;
    const endUtcMs = startUtcMs + DURATION_MINUTES * 60_000;

    function toParisISO(utcMs: number): string {
      const d = new Date(utcMs + offsetMin * 60_000);
      return (
        `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
        `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:00${offsetStr}`
      );
    }

    const labelDay  = FR_DAYS[slotDate.getDay()];
    const labelDate = `${slotDate.getDate()} ${FR_MONTHS[slotDate.getMonth()]}`;
    const labelTime = `${tpl.hour}h${tpl.minute === 0 ? '00' : pad(tpl.minute)}`;
    const value     = `${slotDate.getFullYear()}-${pad(slotDate.getMonth() + 1)}-${pad(slotDate.getDate())}-${pad(tpl.hour)}${pad(tpl.minute)}`;

    return {
      labelDay,
      labelDate,
      labelTime,
      label: `${labelDay} ${labelDate} à ${labelTime}`,
      value,
      startDateTime: toParisISO(startUtcMs),
      endDateTime:   toParisISO(endUtcMs),
    };
  });
}

const NEED_TYPES = [
  'Recrutement',
  'Audit recrutement',
  'Conseil',
  'Autre',
] as const;

// ── Types ──────────────────────────────────────────────────────────────────────

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  role: string;
  needType: string;
  preferredSlot: string; // stores slot.value for selection tracking
  message: string;
  website: string; // honeypot
}

type FieldErrors = Partial<Record<keyof FormState | 'global', string>>;

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  role: '',
  needType: '',
  preferredSlot: '',
  message: '',
  website: '',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function BookingForm() {
  // Pendant le prerender (window.__PRERENDER__, cf. scripts/prerender.mjs),
  // on ne génère AUCUN créneau : sinon les dates du jour de build sont
  // figées dans le HTML statique et apparaissent "dans le passé" aux
  // crawlers / avant hydratation. Les vrais visiteurs (JS) recalculent
  // toujours depuis leur `new Date()`.
  const isPrerender =
    typeof window !== 'undefined' &&
    (window as unknown as { __PRERENDER__?: boolean }).__PRERENDER__ === true;
  const slots = useMemo(() => (isPrerender ? [] : buildSlots()), [isPrerender]);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate(): boolean {
    const next: FieldErrors = {};

    if (!form.firstName.trim()) next.firstName = 'Le prénom est obligatoire.';
    if (!form.lastName.trim()) next.lastName = 'Le nom est obligatoire.';
    if (!form.email.trim() || !EMAIL_RE.test(form.email))
      next.email = 'Adresse email invalide.';
    if (!form.phone.trim() || !PHONE_RE.test(form.phone))
      next.phone = 'Numéro invalide (10 chiffres minimum).';
    if (!form.company.trim()) next.company = 'La société est obligatoire.';
    if (!form.needType) next.needType = 'Veuillez choisir un type de besoin.';
    if (!form.preferredSlot)
      next.preferredSlot = 'Veuillez choisir un créneau souhaité.';

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Honeypot — bot filled the hidden field
    if (form.website) return;

    if (!validate() || submitting) return;

    setSubmitting(true);
    setErrors({});

    const selectedSlot = slots.find((s) => s.value === form.preferredSlot)!;

    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      company: form.company.trim(),
      role: form.role.trim(),
      needType: form.needType,
      preferredSlotLabel: selectedSlot.label,
      preferredSlotValue: selectedSlot.value,
      preferredSlotStartDateTime: selectedSlot.startDateTime,
      preferredSlotEndDateTime: selectedSlot.endDateTime,
      durationMinutes: DURATION_MINUTES,
      message: form.message.trim() || undefined,
      source: 'recruteurs.io',
      submittedAt: new Date().toISOString(),
    };

    try {
      const res = await fetch('/.netlify/functions/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`Erreur serveur : ${res.status}`);
      }

      setStatus('success');
      setForm(EMPTY);
    } catch {
      setStatus('error');
      setErrors({
        global:
          'Une erreur est survenue lors de l\'envoi. Veuillez réessayer ou nous contacter directement.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  // ── Success state ──────────────────────────────────────────────────────────

  if (status === 'success') {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-10 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircle className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-emerald-900">
          Demande bien transmise !
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-emerald-700">
          Votre demande de rendez-vous a bien été transmise.
          <br />
          Vous recevrez une confirmation rapidement.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-7 inline-flex items-center gap-2 rounded-xl border border-emerald-300 px-5 py-2.5 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-100"
        >
          Faire une nouvelle demande
        </button>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  return (
    <div className="rounded-3xl border border-neutral-200 bg-white p-8 md:p-10 shadow-sm">

      {/* Honeypot — hidden from real users */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={(e) => set('website', e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        style={{ display: 'none' }}
        aria-hidden="true"
      />

      {/* Global error */}
      {errors.global && (
        <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm text-red-700">{errors.global}</p>
          <button
            type="button"
            onClick={() => { setErrors({}); setStatus('idle'); }}
            className="ml-auto shrink-0 text-red-400 hover:text-red-600"
            aria-label="Fermer le message d'erreur"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-5">

        {/* Prénom / Nom */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Prénom" required error={errors.firstName}>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => set('firstName', e.target.value)}
              placeholder="Marie"
              className={inputCls(!!errors.firstName)}
              autoComplete="given-name"
            />
          </Field>
          <Field label="Nom" required error={errors.lastName}>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => set('lastName', e.target.value)}
              placeholder="Dupont"
              className={inputCls(!!errors.lastName)}
              autoComplete="family-name"
            />
          </Field>
        </div>

        {/* Email / Téléphone */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email" required error={errors.email}>
            <input
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="marie.dupont@societe.com"
              className={inputCls(!!errors.email)}
              autoComplete="email"
            />
          </Field>
          <Field label="Téléphone" required error={errors.phone}>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="06 12 34 56 78"
              className={inputCls(!!errors.phone)}
              autoComplete="tel"
            />
          </Field>
        </div>

        {/* Société / Fonction */}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Société" required error={errors.company}>
            <input
              type="text"
              value={form.company}
              onChange={(e) => set('company', e.target.value)}
              placeholder="Acme SAS"
              className={inputCls(!!errors.company)}
              autoComplete="organization"
            />
          </Field>
          <Field label="Fonction" error={errors.role}>
            <input
              type="text"
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              placeholder="DRH, Directeur, Gérant…"
              className={inputCls(!!errors.role)}
              autoComplete="organization-title"
            />
          </Field>
        </div>

        {/* Type de besoin */}
        <Field label="Type de besoin" required error={errors.needType}>
          <div className="flex flex-wrap gap-2">
            {NEED_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => set('needType', type)}
                className={`rounded-xl border px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  form.needType === type
                    ? 'border-[#d03484] bg-gradient-to-r from-[#d03484]/10 to-[#d47254]/10 text-[#d03484] ring-1 ring-[#d03484]'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:border-[#d03484]/50 hover:text-[#d03484]'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </Field>

        {/* Créneau souhaité */}
        <Field label="Créneau souhaité" required error={errors.preferredSlot}>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-5">
            {slots.length === 0 && (
              // État prerender / pré-hydratation : jamais de dates figées
              <div className="col-span-full rounded-2xl border border-neutral-200 bg-[#fbfaf8] px-4 py-4 text-center text-xs text-neutral-400">
                Chargement des prochains créneaux disponibles…
              </div>
            )}
            {slots.map((slot) => (
              <button
                key={slot.value}
                type="button"
                onClick={() => set('preferredSlot', slot.value)}
                className={`flex flex-col items-center gap-1.5 rounded-2xl border px-3 py-3.5 text-xs font-medium transition-all duration-200 ${
                  form.preferredSlot === slot.value
                    ? 'border-[#d03484] bg-gradient-to-b from-[#d03484]/10 to-[#d47254]/10 text-[#d03484] ring-1 ring-[#d03484]'
                    : 'border-neutral-200 bg-[#fbfaf8] text-neutral-600 hover:border-[#d03484]/50 hover:text-[#d03484]'
                }`}
              >
                <Clock className="h-4 w-4 shrink-0" />
                <span className="font-semibold leading-tight text-center">
                  {slot.labelDay} {slot.labelDate}
                </span>
                <span className="text-[11px] leading-none opacity-80">{slot.labelTime}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-neutral-400">
            Ce créneau est indicatif. Nous confirmerons la disponibilité par email.
          </p>
        </Field>

        {/* Message libre */}
        <Field label="Message (optionnel)" error={errors.message}>
          <textarea
            value={form.message}
            onChange={(e) => set('message', e.target.value)}
            rows={4}
            placeholder="Décrivez brièvement votre contexte, votre besoin ou vos questions…"
            className={`${inputCls(!!errors.message)} resize-y`}
          />
        </Field>

        {/* Disclaimer */}
        <p className="text-xs text-neutral-400 leading-relaxed">
          En soumettant ce formulaire, vous acceptez que vos données soient
          traitées par Recruteurs.io dans le cadre de votre demande de
          rendez-vous, conformément à notre{' '}
          <a
            href="/legal/confidentialite"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#d03484]"
          >
            politique de confidentialité
          </a>
          .
        </p>

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-4 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Envoi en cours…
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              Envoyer ma demande de rendez-vous
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-neutral-700">
        {label}
        {required && <span className="ml-0.5 text-[#d03484]"> *</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
          <AlertCircle className="h-3 w-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

function inputCls(hasError: boolean) {
  return `w-full rounded-xl border px-3.5 py-2.5 text-sm text-neutral-900 placeholder-neutral-400 outline-none transition-all focus:ring-1 ${
    hasError
      ? 'border-red-300 bg-red-50 focus:border-red-400 focus:ring-red-200'
      : 'border-neutral-200 bg-white focus:border-[#d03484] focus:ring-[#d03484]/20'
  }`;
}
